import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, Injector, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { ApAlertService } from 'webcommon/core-ui';
import { ServiceSiteMappingDto } from 'webcommon/shared';
import { FcItemModalContext } from '../../../models';
import { FcItemListBaseComponent, FcListValidationService, FormControlExtended } from '../../generic';

export interface ServiceSiteMappingListItemDto {
  ServiceSiteMapping: ServiceSiteMappingDto[];
}

interface ServiceSiteMappingFormGroup {
  mapItemUid: FormControl<string>;
  serviceSitePk: FormControl<string[]>;
  substitutionText: FormControl<string>;
}

function serviceSiteUniqueValidator(subject: BehaviorSubject<string[]>): ValidatorFn {
  return (control: FormControl<string[]>): ValidationErrors | null => {
    const controlPkNotUnique = subject.getValue().some((pk) => control.value.includes(pk));
    return controlPkNotUnique ? { isUnique: true } : null;
  };
}

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-payer-id-override-list',
  templateUrl: './fc-payer-id-override-list.component.html',
  styleUrls: ['./fc-payer-id-override-list.component.less'],
})
export class FcPayerIdOverrideListComponent extends FcItemListBaseComponent implements OnChanges, OnDestroy {
  @Input() baseId: string;
  @Input() form: FormGroup;
  @Input() listItem: ServiceSiteMappingListItemDto;
  @Input() isSubmitted = false;
  @Input() itemContext: FcItemModalContext;

  private serviceSiteNotUniquePksSubject = new BehaviorSubject<string[]>([]);
  public serviceSiteMappingFormArray = new FormArray<FormGroup>([]);

  constructor(injector: Injector) {
    super(injector);
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.form != null) {
      this.addControlsToForm();
    }

    if (changes.itemContext != null || changes.form != null || changes.listItem != null) {
      this.setForm();
    }
  }

  public setForm(): void {
    this.serviceSiteMappingFormArray.clear();

    if (Array.isArray(this.listItem?.ServiceSiteMapping)) {
      const items = [...this.listItem.ServiceSiteMapping];
      items.sort((a, b) => {
        return a.DisplayOrder > b.DisplayOrder ? 1 : -1;
      });
      for (const item of items) {
        this.addServiceSiteMappingRow(item.FindTableToItemUid, item.SubstitutionText);
      }
    }

    this.addServiceSiteMappingRow();
  }

  public addControlsToForm(): void {
    this.form.addControl('ServiceSiteMapping', this.serviceSiteMappingFormArray);
  }

  private setNotUniquePks() {
    const uniquePks: string[] = [];
    const notUniquePks: string[] = [];
    for (const item of this.serviceSiteMappingFormArray.value) {
      if (Array.isArray(item?.serviceSitePk)) {
        for (const pk of item.serviceSitePk) {
          if (typeof pk === 'string') {
            if (!uniquePks.includes(pk)) {
              uniquePks.push(pk);
            } else if (!notUniquePks.includes(pk)) {
              notUniquePks.push(pk);
            }
          }
        }
      }
    }
    this.serviceSiteNotUniquePksSubject.next(notUniquePks);
    this.validateServiceSiteUnique();
  }

  private addServiceSiteMappingRow(serviceSitePk?: string, substitutionText?: string, mapItemUid?: string): void {
    const serviceSiteControl = new FormControlExtended(
      serviceSitePk ? [serviceSitePk] : [],
      serviceSiteUniqueValidator(this.serviceSiteNotUniquePksSubject)
    );
    serviceSiteControl.showErrorFlag = true;
    this.serviceSiteMappingFormArray.push(
      new FormGroup({
        mapItemUid: new FormControl(mapItemUid ?? null),
        serviceSitePk: serviceSiteControl,
        substitutionText: new FormControl(substitutionText ?? '', [Validators.maxLength(200)]),
      })
    );
  }

  public deleteServiceSiteMappingRow(index: number): void {
    this.serviceSiteMappingFormArray.removeAt(index);
    this.setNotUniquePks();
    this.validateServiceSiteUnique();
  }

  private validateServiceSiteUnique(): void {
    for (const formGroup of this.serviceSiteMappingFormArray.controls) {
      formGroup.get('serviceSitePk')?.updateValueAndValidity();
    }
  }

  public getServiceSiteControl(index: number): FormControl<string[]> {
    return this.serviceSiteMappingFormArray.at(index).get('serviceSitePk') as FormControl<string[]>;
  }

  public getSubstitutionTextControl(index: number): FormControl<string> {
    return this.serviceSiteMappingFormArray.at(index).get('substitutionText') as FormControl<string>;
  }

  public reorder(event: CdkDragDrop<ServiceSiteMappingFormGroup>): void {
    const formGroup = this.serviceSiteMappingFormArray.at(event.previousIndex) as FormGroup;
    this.serviceSiteMappingFormArray.removeAt(event.previousIndex);
    this.serviceSiteMappingFormArray.insert(event.currentIndex, formGroup);
  }

  public serviceSiteChanged(): void {
    // Add empty row when every row has Pk
    const isEmptyRow = this.serviceSiteMappingFormArray.value.some((item: any) => {
      return !(typeof item?.serviceSitePk?.[0] === 'string');
    });
    if (!isEmptyRow) {
      this.addServiceSiteMappingRow();
    }

    this.setNotUniquePks();
  }

  public deleteRowButtonVisible(index: number): boolean {
    // eslint-disable-next-line max-len
    const itemsWithoutServiceSitePk = (this.serviceSiteMappingFormArray.value.filter((item: any) => !(typeof item?.serviceSitePk?.[0] === 'string'))).length;
    const currentItemHasServiceSitePk = typeof this.serviceSiteMappingFormArray.value[index]?.serviceSitePk?.[0] === 'string';
    if (itemsWithoutServiceSitePk <= 1) {
      return currentItemHasServiceSitePk;
    }
    return true;
  }
}
